import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lightning, CheckCircle, XCircle, ArrowRight,
  CreditCard, CalendarBlank, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getPlanIcon(sub) {
  if (sub.is_pro) return <Lightning size={24} weight="fill" className="text-yellow-400" />;
  if (sub.is_admin) return <Lightning size={24} weight="fill" className="text-green-400" />;
  return <Lightning size={24} className="text-neutral-500" />;
}

function getPlanName(sub) {
  if (sub.is_admin) return "Admin";
  if (sub.is_pro) return "Pro";
  return "Free";
}

function getPlanDesc(sub) {
  if (sub.is_admin) return "Unlimited access";
  if (sub.is_pro) return "$29.99/month";
  return "Limited daily access";
}

function getPlanBadgeStyle(sub) {
  if (sub.is_admin) return "text-green-400 border-green-800";
  if (sub.is_pro) return "text-yellow-400 border-yellow-800";
  return "text-neutral-400 border-neutral-700";
}

function getPlanBadgeLabel(sub) {
  if (sub.is_admin) return "Admin";
  if (sub.is_pro) return "Active";
  return "Free";
}

export function SubscriptionManager({ onClose }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payments/subscription`);
      setSub(data);
    } catch {
      toast.error("Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`, {
        return_url: window.location.href,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.info(data.message || "Billing portal not available");
      }
    } catch (err) {
      toast.error("Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const cancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/payments/cancel`);
      toast.success("Subscription cancelled. You've been moved to the free tier.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel subscription");
      setCancelling(false);
    }
  };

  const resubscribe = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/resubscribe`, {
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start resubscription");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="subscription-loading">
        <div className="bg-neutral-950 border border-neutral-800 p-8 max-w-md w-full text-center">
          <p className="text-neutral-400 text-sm">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (!sub) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="subscription-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-neutral-950 border border-neutral-800 max-w-lg w-full"
      >
        {/* Header */}
        <div className="p-6 border-b border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard size={28} weight="duotone" className="text-neutral-300" />
              <div>
                <h2 className="text-xl font-bold" data-testid="subscription-title">Manage Subscription</h2>
                <p className="text-sm text-neutral-400">Your billing and plan details</p>
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white text-sm" data-testid="subscription-close">
              Close
            </button>
          </div>
        </div>

        {/* Current Plan */}
        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between p-4 border border-neutral-800">
            <div className="flex items-center gap-3">
              {getPlanIcon(sub)}
              <div>
                <p className="font-semibold" data-testid="current-plan-name">
                  {getPlanName(sub)}
                </p>
                <p className="text-xs text-neutral-400">
                  {getPlanDesc(sub)}
                </p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 border ${getPlanBadgeStyle(sub)}`} data-testid="current-plan-badge">
              {getPlanBadgeLabel(sub)}
            </span>
          </div>

          {/* Pro Details */}
          {sub.can_cancel && sub.last_payment && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-400">Member since</span>
                <span data-testid="pro-since-date">
                  {sub.pro_since ? new Date(sub.pro_since).toLocaleDateString() : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-400">Last payment</span>
                <span data-testid="last-payment-amount">${sub.last_payment.amount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-400">Payment date</span>
                <span data-testid="last-payment-date">
                  {sub.last_payment.date ? new Date(sub.last_payment.date).toLocaleDateString() : "—"}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            {sub.can_cancel && (
              <>
                <Button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white h-11"
                  data-testid="billing-portal-button"
                >
                  <CreditCard size={18} className="mr-2" />
                  {portalLoading ? "Opening portal..." : "Manage Billing"}
                </Button>

                {!showConfirm ? (
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="w-full text-center text-sm text-red-400 hover:text-red-300 py-2 transition-colors"
                    data-testid="cancel-subscription-button"
                  >
                    Cancel subscription
                  </button>
                ) : (
                  <AnimatePresence>
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="border border-red-900/50 bg-red-900/10 p-4"
                      data-testid="cancel-confirm-section"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Warning size={18} className="text-red-400" />
                        <p className="text-sm font-medium text-red-400">Cancel Pro?</p>
                      </div>
                      <p className="text-xs text-neutral-400 mb-4">
                        You'll lose 10x limits and be moved back to the free tier immediately.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={cancelSubscription}
                          disabled={cancelling}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm h-9"
                          data-testid="confirm-cancel-button"
                        >
                          {cancelling ? "Cancelling..." : "Yes, cancel"}
                        </Button>
                        <Button
                          onClick={() => setShowConfirm(false)}
                          variant="outline"
                          className="flex-1 border-neutral-700 text-sm h-9"
                          data-testid="keep-subscription-button"
                        >
                          Keep Pro
                        </Button>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}
              </>
            )}

            {!sub.is_pro && !sub.is_admin && (
              <Button
                onClick={resubscribe}
                className="w-full bg-white text-black hover:bg-neutral-200 h-11 font-semibold"
                data-testid="resubscribe-button"
              >
                <Lightning size={18} weight="fill" className="mr-2 text-yellow-600" />
                {user?.pro_cancelled_at ? "Resubscribe to Pro — $29.99/mo" : "Upgrade to Pro — $29.99/mo"}
              </Button>
            )}

            {sub.is_admin && (
              <p className="text-xs text-neutral-500 text-center">
                Admin accounts have unlimited access. No subscription needed.
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
